// DEPENDENCIES
import _ from "lodash";

// COMPONENTS
import {FieldType} from "../../../../../../../../utils/forms/components/FormGrid";
import {SubFormDefinition} from "../../../../../../../../utils/forms/components/FormSet";

// STORE
import {ProgressiveRulesSchema} from "../../../../../../reducers/schema.reducer";
import {SelectedGroupsByProgressive} from "../../../../../../models/SelectedGroupsByProgressive";
import {
    GROUP_TYPE,
    TerminalGroupConfiguration,
    GameGroupConfiguration
} from "../../../../../../reducers/editingConfiguration.reducer";

// UTILS
import groupListOfType from "../../../../../Utils/full-group-list";
import getSelectableCoins from "../../../../../Utils/GetSelectableCoins/get-selectable-coins";
import {parseAvailableGroups} from "../../../../../Utils/parse-available-groups";
import buildFormGridFields from "../../../../../../../../utils/forms/builders/build-form-grid-fields";
import buildSchemaCompliantDefinition
    from "../../../../../../../../utils/forms/builders/build-schema-compliant-definition";
import {SCHEMA_NAMES} from "../ProgressivesConfigurationSection.constants";

// CONTANTS AND DEFAULTS
import * as CONFIG_CONSTANTS from "../ProgressivesConfigurationSection.constants";
import {
    defaultsWays,
    defaultsPrize
} from "../ProgressivesConfigurationSection.defaults";
import React from "react";

export const waysSubforms = props =>
    _.memoize(
        ({
             terminalGroups,
             gameGroups,
             selectedTerminalGroups,
             selectedGameGroups,
             schema,
             selectedGroupType,
             progressivesConfig,
             type,
             rules,
             subsidiary,
             gameInstances
         }): SubFormDefinition[] => {
            const coins = getSelectableCoins(props);
            let specificInfo = {};

            specificInfo = {
                formType: type,
                addLabel: CONFIG_CONSTANTS.FORM_TYPE_NAMES[type],
                type: type,
                progressive_types: CONFIG_CONSTANTS.PROGRESSIVE_CHILDREN_TYPES,
                child_progressive_types:
                    CONFIG_CONSTANTS.PROGRESSIVE_CHILDREN_TYPES.filter(
                        type => type.value == "proportional"
                    ),
                prefix: CONFIG_CONSTANTS.FORM_TYPE_NAMES[type],
                addChildren: type != "levels" ? true : false
            };

            return [
                _createWaysSubform(
                    schema,
                    terminalGroups,
                    gameGroups,
                    selectedTerminalGroups,
                    selectedGameGroups,
                    selectedGroupType,
                    progressivesConfig,
                    coins,
                    rules,
                    specificInfo,
                    type,
                    subsidiary,
                    gameInstances
                )
            ];
        }
    );

function _createWaysSubform(
    schema: any,
    terminalGroups: TerminalGroupConfiguration[],
    gameGroups: GameGroupConfiguration[],
    selectedTerminalGroups: SelectedGroupsByProgressive,
    selectedGameGroups: SelectedGroupsByProgressive,
    selectedGroupType: GROUP_TYPE,
    progressivesConfig,
    coins,
    rules: ProgressiveRulesSchema,
    specificInfo,
    type,
    subsidiary,
    gameInstances
): SubFormDefinition {
    let topSchema = _.cloneDeep(schema.definitions.$progressive);
    let childrenPresetSchema = _.cloneDeep(schema.definitions.$progressive);
    if (type === 'sap') {
        topSchema = _.cloneDeep(schema.definitions.$sap_jackpot);
        childrenPresetSchema = _.cloneDeep(schema.definitions.$sap_jackpot);
    } else if (type === 'multipots') {
        topSchema = _.cloneDeep(schema.definitions.$multipots);
        childrenPresetSchema = _.cloneDeep(schema.definitions.$multipots);
    } else if (type === 'jumbolink') {
        topSchema = _.cloneDeep(schema.definitions.$jumbolink);
        childrenPresetSchema = _.cloneDeep(schema.definitions.$jumbolink);
    } else if (type === 'reelstrike') {
        topSchema = _.cloneDeep(schema.definitions.$reelstrike);
        childrenPresetSchema = _.cloneDeep(schema.definitions.$reelstrike);
    } else if (type === 'luckytokenslink') {
        topSchema = _.cloneDeep(schema.definitions.$luckytokenslink);
        childrenPresetSchema = _.cloneDeep(schema.definitions.$luckytokenslink);
    }

    topSchema.properties["id"]["unique"] = progressivesConfig.filter(progressive => progressive.type === type).map(progressive => progressive.id);

    const fullGroupList = groupListOfType(
        selectedGroupType,
        terminalGroups,
        gameGroups
    );

    const selectedGroupList = parseAvailableGroups(
        terminalGroups,
        selectedTerminalGroups,
        gameGroups,
        selectedGameGroups,
        specificInfo.type,
        selectedGroupType
    );

    const exclusiveFields = Object.keys(topSchema.properties).filter(
        property => {
            return (
                topSchema.properties[property].hasOwnProperty("exclusive") &&
                topSchema.properties[property].exclusive == true
            );
        }
    );

    const groupTypeFormDefinition = CONFIG_CONSTANTS.progressiveFormDefinition(
        selectedGroupType,
        coins,
        fullGroupList,
        selectedGroupList,
        specificInfo.progressive_types,
        rules.presets
            .filter(preset =>
                specificInfo.type == "levels"
                    ? true
                    : preset.stype == "parent" || preset.stype == null
            )
            .map(preset => {
                return {
                    value: preset.id,
                    label: preset.name,
                    individual_pot: preset.prizes ? preset.prizes[0].individual_pot : null,
                    certified: preset.certified,
                    notes: preset.notes
                };
            }),
        undefined,
        exclusiveFields,
        specificInfo.formType == 'sap' || specificInfo.formType == "easylink" || specificInfo.formType == "luckytokenslink" || specificInfo.type == "reelstrike",
        specificInfo.formType
    );

    let unconditionalFields = [
        CONFIG_CONSTANTS.SCHEMA_NAMES.ID,
        CONFIG_CONSTANTS.SCHEMA_NAMES.PRIZES
    ];
    // multipots
    // reelstrike
    if (type !== 'multipots' && type !== 'reelstrike' && type !== 'luckytokenslink') {
        unconditionalFields = [
            CONFIG_CONSTANTS.SCHEMA_NAMES.ID,
            CONFIG_CONSTANTS.SCHEMA_NAMES.COIN,
            CONFIG_CONSTANTS.SCHEMA_NAMES.PRIZES
        ]
    }
    const topFormDefinition = buildSchemaCompliantDefinition(
        topSchema,
        groupTypeFormDefinition,
        unconditionalFields
    );

    let extraRequiredFields = ["preset"];
    // multipots
    // reelstrike
    if (type !== 'multipots' && type !== 'reelstrike' && type !== 'luckytokenslink') {
        extraRequiredFields = ["preset", "coin"];
        topFormDefinition["coin"].definition.disabled = true;
    }

    if (type == 'reelstrike' || type == 'luckytokenslink') {
        extraRequiredFields = [];
        delete topFormDefinition["preset"];
        delete topFormDefinition["coin"];
    }

    return {
        formType: specificInfo.formType,
        addLabel: specificInfo.addLabel,
        emptyValue: defaultsWays({
            schema: schema,
            progressivesConfig,
            type: specificInfo.type,
            prefix: specificInfo.prefix,
            isParent: true,
            hasPreset: true,
            rules,
            schemaKey:
                specificInfo.type == "sap" ? "$sap_jackpot"
                    : specificInfo.type == "multipots" ? "$multipots"
                        : specificInfo.type == "jumbolink" ? "$jumbolink"
                            : specificInfo.type == "reelstrike" ? "$reelstrike"
                                : specificInfo.type == "luckytokenslink" ? "$luckytokenslink"
                                    : "$progressive"
        }),
        fields: [
            ...buildFormGridFields(
                topSchema,
                topFormDefinition,
                schema,
                extraRequiredFields
            ),
            createPrizesSubForm(
                schema,
                selectedGroupType,
                rules,
                specificInfo.progressive_types,
                specificInfo.child_progressive_types,
                specificInfo.type == "sap" ? "$sap_jackpot"
                    : specificInfo.type == "multipots" ? "$multipots"
                        : specificInfo.type == "jumbolink" ? "$jumbolink"
                            : specificInfo.type == "reelstrike" ? "$reelstrike"
                                : specificInfo.type == "luckytokenslink" ? "$luckytokenslink"
                                    : "$progressive",
                false,
                [],
                specificInfo.type == "sap" || specificInfo.type == "easylink",
                specificInfo.type,
                subsidiary
            ).form,
            createChildrenSubForm(
                childrenPresetSchema,
                schema,
                selectedGroupType,
                rules,
                progressivesConfig,
                specificInfo.child_progressive_types,
                exclusiveFields,
                fullGroupList,
                selectedGroupList,
                specificInfo.type,
                coins,
                specificInfo.addChildren,
                subsidiary,
                gameInstances
            )
        ]
    };
}

export const createPrizesSubForm = (
    schema,
    selectedGroupType,
    rules,
    proportionalTypes,
    child_progressive_types,
    schemaKey,
    altTitle?: boolean,
    excludeFields?: string[],
    hasIndividualPot?: boolean,
    formType?: any,
    subsidiary?: any
) => {
    let prizesSchema = schema.definitions[schemaKey].properties.prizes.items;

    const unconditionalFields = formType === "reelstrike" ?
        [SCHEMA_NAMES.INDIVIDUAL_POT] :
        (formType === "easylink" || formType === "luckytokenslink") ?
            [SCHEMA_NAMES.INDIVIDUAL_POT, SCHEMA_NAMES.GAME_SPECIFIC_SETTINGS] :
            [];

    const prizeDefinition = buildSchemaCompliantDefinition(
        prizesSchema,
        CONFIG_CONSTANTS.progressiveFormDefinition(
            selectedGroupType,
            undefined,
            undefined,
            undefined,
            !altTitle ? proportionalTypes : child_progressive_types,
            undefined,
            {
                max_bet: "Max. Proportional Bet",
                max_bet_placeholder: "Insert Max. Proportional Bet"
            },
            [],
            hasIndividualPot,
            formType
        ),
        unconditionalFields
    );

    if (excludeFields && excludeFields.length > 0) {
        for (let i = 0; i < excludeFields.length; i++) {
            if (prizeDefinition.hasOwnProperty(excludeFields[i])) {
                delete prizeDefinition[excludeFields[i]];
            }
        }
    }

    prizeDefinition["initial_value"].definition.fieldOverrides.columnClass =
        "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4";
    prizeDefinition["contribution"].definition.fieldOverrides.columnClass =
        "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4 percentage";
    prizeDefinition["max_value"].definition.fieldOverrides.columnClass =
        "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4";
    if (prizeDefinition.hasOwnProperty("min_game_mode")) {
        prizeDefinition["min_game_mode"].definition.fieldOverrides.columnClass =
            `${prizeDefinition["min_game_mode"].definition.fieldOverrides.columnClass} col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4`;
        prizeDefinition["min_game_mode"].definition.disabled = true;
        prizeDefinition["min_game_mode"].order = 8;
    }
    if (prizeDefinition.hasOwnProperty("max_proportional_total_bet")) {
        prizeDefinition["max_proportional_total_bet"].definition.fieldOverrides.columnClass =
            `${prizeDefinition["max_proportional_total_bet"].definition.fieldOverrides.columnClass} col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4`;
        prizeDefinition["max_proportional_total_bet"].definition.disabled = true;
        prizeDefinition["max_proportional_total_bet"].order = 9;
    }
    prizeDefinition[
        "play_jackpot_sound"
        ].definition.fieldOverrides.columnClass =
        "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4";
    prizeDefinition["play_jackpot_sound"].order = 11;
    prizeDefinition[
        "increment_sound_value"
        ].definition.fieldOverrides.columnClass =
        "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4";
    prizeDefinition["increment_sound_value"].order = 10;


    if (prizeDefinition.hasOwnProperty("min_bet")) {
        prizeDefinition["min_bet"].definition.fieldOverrides.columnClass =
            "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4";
        prizeDefinition["min_bet"].definition.disabled = true;
    }

    if (prizeDefinition.hasOwnProperty("max_bet")) {
        prizeDefinition["max_bet"].definition.fieldOverrides.columnClass =
            "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4";
        prizeDefinition["max_bet"].definition.disabled = true;
    }

    if (prizeDefinition.hasOwnProperty("proportion_type")) {
        prizeDefinition[
            "proportion_type"
            ].definition.fieldOverrides.columnClass =
            "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4";
        prizeDefinition["proportion_type"].definition.disabled = true;
    }

    if (prizeDefinition.hasOwnProperty("reserve_contribution")) {
        prizeDefinition[
            "reserve_contribution"
            ].definition.fieldOverrides.columnClass =
            "col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4";
        prizeDefinition["reserve_contribution"].definition.disabled = true;
    }

    if (prizeDefinition.hasOwnProperty("individual_pot")) {
        prizeDefinition[
            "individual_pot"
            ].definition.fieldOverrides.columnClass = `${prizeDefinition[
            "individual_pot"
            ].definition.fieldOverrides.columnClass} col-xs-12 col-sm-6 col-md-4 col-lg-4 col-xl-4`;
        prizeDefinition["individual_pot"].definition.disabled = true;
    }

    let extraFields = undefined;
    prizeDefinition["initial_value"].definition.disabled = true;
    prizeDefinition["contribution"].definition.disabled = true;
    prizeDefinition["max_value"].definition.disabled = true;

    let fields = buildFormGridFields(prizesSchema, prizeDefinition);

    const minBetIndex = fields.findIndex(obj => obj.name == "min_bet");
    const maxBetIndex = fields.findIndex(obj => obj.name == "max_bet");

    if (minBetIndex > -1 && maxBetIndex > -1) {
        extraFields = {
            min_bet: fields[minBetIndex],
            max_bet: fields[maxBetIndex]
        };

        //fields.splice(minBetIndex, 1);
        //fields.splice(maxBetIndex, 1);
    }

    const emptyValues = defaultsPrize({
        schema: prizesSchema.properties,
        index: rules.prizes[0]
    });

    return {
        form: {
            name: "prizes",
            fieldType: "formSet" as FieldType,
            fieldOptions: {
                id: "ProgPrizes",
                subFormTypes: [
                    {
                        formType: "prize",
                        disableAdd: true,
                        hideDelete: true,
                        emptyValue: emptyValues,
                        fields
                    }
                ],
                formSetLabel: altTitle ? "Prizes" : formType == 'reelstrike' || formType == 'luckytokenslink' ? '' : "Shared Prize",
                initialValues: [],
                subsidiary: subsidiary
            },
            formSpacing: false,
            columnClass: "col-12"
        },
        extraFields
    };
};

export const createChildrenSubForm = (
    levelsTopSchema,
    schema,
    selectedGroupType,
    rules,
    progressivesConfig,
    child_progressive_types,
    exclusiveFields,
    fullGroupList,
    selectedGroupList,
    type,
    coins,
    enableAdd?: boolean,
    subsidiary?: any,
    gameInstances?: any
) => {
    const childRules = {
        ...rules,
        presets: rules.presets.filter(
            rule => rule.stype == "child" || rule.stype == null
        )
    };

    const emptyValues = defaultsWays({
        schema: schema,
        progressivesConfig,
        type: type == "levels" ? "progressive_group_levels" : "progressive_group",
        prefix: "prog-group",
        isParent: false,
        hasPreset: false,
        rules: childRules,
        schemaKey: type == "sap" ? "$sap_jackpot"
            : type == "multipots" ? "$multipots"
                : type == "jumbolink" ? "$jumbolink"
                    : type == "reelstrike" ? "$reelstrike"
                        : type == "luckytokenslink" ? "$luckytokenslink"
                            : "$progressive"

    });

    const tempNewTopSchema = _.cloneDeep(levelsTopSchema);
    delete tempNewTopSchema.properties.coin;

    if (type == "levels") {
        delete tempNewTopSchema.properties.preset;
    }

    const requiredGroupFields =
        selectedGroupType == null
            ? "groups_placeholder"
            : selectedGroupType == GROUP_TYPE.GAME
                ? "game_groups"
                : "terminal_groups";

    let newTopSchema = {
        ...tempNewTopSchema,
        required: [...tempNewTopSchema.required, requiredGroupFields]
    };

    if (selectedGroupType == null) {
        newTopSchema.properties = {
            ...newTopSchema.properties,
            [requiredGroupFields]: {type: "string"}
        };
    } else {
        newTopSchema.properties = {
            ...newTopSchema.properties,
            [requiredGroupFields]: newTopSchema.properties[
                requiredGroupFields
                ].oneOf.find(objt => objt.type !== "null" && objt.type !== null)
        };
    }

    let filteredPresets = rules.presets
        .filter(preset => preset.stype == "child" || preset.stype == null)
        .map(preset => {
            return {
                value: preset.id,
                label: preset.name,
                individual_pot: preset.prizes ? preset.prizes[0].individual_pot : null,
                certified: preset.certified,
                notes: preset.notes
            };
        })

    // preset id = 0 que significa que não vai ser associado ao pote este prog. group
    if (["multipots", "jumbolink", "easylink"].includes(type)){
        filteredPresets.unshift(
            {
                "value": 0,
                "label": "Not Applicable",
                "certified": true
            }
        );
    }

    const groupTypeFormDefinition = CONFIG_CONSTANTS.progressiveFormDefinition(
        selectedGroupType,
        type == 'reelstrike' ? coins : undefined,
        fullGroupList,
        selectedGroupList,
        child_progressive_types,
        filteredPresets,
        undefined,
        exclusiveFields,
        type == 'sap' || type == "easylink" || type == "luckytokenslink",
        type
    );

    const topFormDefinition = buildSchemaCompliantDefinition(
        newTopSchema,
        groupTypeFormDefinition,
        [
            CONFIG_CONSTANTS.SCHEMA_NAMES.ID,
            CONFIG_CONSTANTS.SCHEMA_NAMES.GROUPS,
            CONFIG_CONSTANTS.SCHEMA_NAMES.PRIZES
        ]
    );

    if (type == "levels") {
        topFormDefinition["id"].definition.fieldOverrides.columnClass =
            "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 hidden";
    }

    let enabledPresetFields = true;

    const prizeSubForm = createPrizesSubForm(
        schema,
        selectedGroupType,
        childRules,
        undefined,
        child_progressive_types,
        type == "sap" ? "$sap_jackpot"
            : type == "multipots" ? "$multipots"
                : type == "jumbolink" ? "$jumbolink"
                    : type == "reelstrike" ? "$reelstrike"
                        : type == "luckytokenslink" ? "$luckytokenslink"
                            : "$progressive",
        enabledPresetFields,
        [],
        type == 'sap' || type == "easylink" || type == "luckytokenslink" || type == "reelstrike",
        type,
        subsidiary
    );


    return {
        name: "children",
        fieldType: "formSetWays" as FieldType,
        fieldOptions: {
            id: "ProgChildren",
            presets: childRules,
            extraFields: prizeSubForm.extraFields,
            subFormTypes: [
                {
                    formType: "progressive_group",
                    disableAdd: type == 'reelstrike' ? true : !enableAdd,
                    hideDelete: type == 'reelstrike' ? true : !enableAdd,
                    emptyValue: emptyValues,
                    fields: [
                        ...buildFormGridFields(
                            newTopSchema,
                            topFormDefinition,
                            schema
                        ),
                        prizeSubForm.form
                    ]
                }
            ],
            formSetLabel: "Progressive Groups",
            gameInstances: gameInstances,
        },
        formSpacing: false,
        columnClass: "col-12"
    };
};
